import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import SimuladorAbertura from "./SimuladorAbertura";
import SimuladorDas from "./SimuladorDas";
import SimuladorDasn from "./SimuladorDasn";
import { Cabine, Numeral } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import { lerParametrosDas } from "../lib/simuladorMei";
import type { ParametrosDas } from "../lib/dasMei";

// O Simulador MEI: as três rotinas do MEI, despachadas pelo primeiro segmento
// depois do slug — `abertura`, `das` e `dasn`.
//
// ---------------------------------------------------------------------------
// De onde isto veio
// ---------------------------------------------------------------------------
// Portado do app externo `simulador-mei.contabilidadefacilitada.com` (Lovable,
// TanStack Start), a pedido de Victor em 24/08. O que mudou no caminho, e por
// quê, está em `docs/runbooks/simulador-mei.md`. Em resumo: a lista de ocupações
// passou a ser a do hub (000098) em vez de um `.ts` de 434 linhas extraídas de
// PDF; os valores do DAS saíram do código para `mei_parametros`; ICMS e ISS
// passaram a sair do FATO da ocupação em vez de um `startsWith` na descrição do
// CNAE; a lista de anos da DASN virou derivada do calendário; e os três
// documentos deixaram de ser HTML interpolado cru.
//
// ---------------------------------------------------------------------------
// O redesenho de 26/08 — o último dos cinco
// ---------------------------------------------------------------------------
// Victor: "falta só o Simulador MEI. Dá um tchan no design dele também". A
// captura do antes mostrou o mesmo diagnóstico das outras quatro (zero navy, zero
// peça do hub) e um defeito de verdade: os três cards da entrada usavam a utility
// `porta` SEM `surface`, e `porta` só dá COMPORTAMENTO — o hover que levanta e a
// soleira laranja. Sem fundo, sem borda e sem sombra, os três não pareciam
// cards: eram três colunas de texto soltas no cinza da página. É a cicatriz
// registrada no CLAUDE.md ("utility que só dá comportamento precisa de par"),
// aqui pelo caminho brando — no HUB DP ela apareceu como título ilegível sobre
// navy.
//
// O que mudou:
//
//  · **A cabine navy com três fatos REAIS do banco** (`mei_parametros`): o INSS
//    do MEI deste ano, o teto anual e o teto do caminhoneiro. São os números que
//    o aluno vem conferir, e eles expiram em janeiro — mostrá-los na entrada é o
//    oposto de widget com dado inventado;
//  · **os três cards ganharam `surface`** (e o numeral de revista, e ícone SVG no
//    lugar do emoji — emoji de sistema muda de forma em cada plataforma e some
//    no headless);
//  · **cada card diz o que PRODUZ** (CCMEI, DAS, recibo de entrega): é o que
//    separa as três rotinas na cabeça de quem escolhe.
//
// ---------------------------------------------------------------------------
// Estado EFÊMERO, por decisão
// ---------------------------------------------------------------------------
// Nada do que o aluno preenche é gravado — nem simulação, nem rascunho, nem
// histórico. É como o app de origem funciona e foi a escolha de Victor no porte.
// Consequência que a tela precisa respeitar: recarregar a página perde tudo, e é
// por isso que a abertura avisa antes de sair e o DAS não promete "continuar
// depois".
export default function SimuladorMei() {
  const { "*": resto = "" } = useParams();
  const primeiro = resto.split("/").filter(Boolean)[0] ?? null;

  if (primeiro === "abertura") return <SimuladorAbertura />;
  if (primeiro === "das") return <SimuladorDas />;
  if (primeiro === "dasn") return <SimuladorDasn />;

  return <Entrada />;
}

const ROTINAS = [
  {
    para: "/ferramenta/simulador-mei/abertura",
    icone: <IconeCarteira />,
    titulo: "Abertura do CNPJ",
    produz: "Emite o CCMEI",
    descricao:
      "Percorra o processo de inscrição do MEI: acesso gov.br, identificação do " +
      "empresário, atividades econômicas, endereços, declarações obrigatórias e a " +
      "emissão do CCMEI.",
    cta: "Abrir um MEI",
  },
  {
    para: "/ferramenta/simulador-mei/das",
    icone: <IconeBoleto />,
    titulo: "Apuração do DAS",
    produz: "Emite o DAS",
    descricao:
      "Gere o Documento de Arrecadação do Simples Nacional mês a mês, com INSS, " +
      "ICMS e ISS conforme a atividade — e veja a composição por código de tributo.",
    cta: "Apurar o DAS",
  },
  {
    para: "/ferramenta/simulador-mei/dasn",
    icone: <IconeDeclaracao />,
    titulo: "Declaração anual",
    produz: "Emite o recibo",
    descricao:
      "Preencha a Declaração Anual do Simples Nacional para o MEI, veja o que " +
      "acontece quando a receita passa do teto e emita o recibo de entrega.",
    cta: "Declarar",
  },
];

function Entrada() {
  // Os parâmetros do banco, para os fatos da cabine. Falha silenciosa de
  // propósito: sem eles a tela some com os três tiles e continua inteira — o
  // valor desta entrada são as três portas, não os números. Errar para menos aqui
  // é invisível; um número errado, não.
  const [p, setP] = useState<ParametrosDas | null>(null);
  useEffect(() => {
    let vivo = true;
    void lerParametrosDas().then((r) => {
      if (vivo) setP(r);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const reais = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }).replace(/[  ]/g, " ");
  const pct = (v: number) => `${(v * 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;

  return (
    <main className="mx-auto max-w-5xl px-4 py-7 sm:px-6">
      <Cabine
        rotulo="Simulador"
        titulo="Simulador MEI"
        descricao="Pratique as três rotinas do Microempreendedor Individual num ambiente que imita o Portal do Empreendedor, o PGMEI e a DASN-SIMEI — sem risco de gerar nada de verdade."
        fatos={
          p ? (
            <>
              <TileDeFato
                rotulo="INSS do MEI"
                valor={reais(p.inss_geral)}
                apoio={`${pct(p.aliquota_inss_geral)} do salário mínimo`}
              />
              <TileDeFato
                rotulo="Teto anual"
                valor={reais(p.teto)}
                apoio="acima disso, desenquadra"
              />
              <TileDeFato
                rotulo="Teto caminhoneiro"
                valor={reais(p.teto_caminhoneiro)}
                apoio={`INSS de ${pct(p.aliquota_inss_caminhoneiro)}`}
              />
            </>
          ) : undefined
        }
      />

      <ul className="mt-6 grid gap-4 md:grid-cols-3">
        {ROTINAS.map((r, i) => (
          <li key={r.para} className="contents">
            {/* `surface porta rise` — os TRÊS juntos. `porta` sozinha não pinta
                fundo nenhum (só o hover e a soleira laranja), e era assim que
                estes cards estavam: sem fundo, sem borda, sem sombra. */}
            <Link
              to={r.para}
              className="surface porta rise flex flex-col gap-3 p-5"
              style={{ "--i": i } as React.CSSProperties}
            >
              <div className="flex items-start justify-between gap-3">
                <span
                  aria-hidden="true"
                  className="grid size-12 shrink-0 place-items-center rounded-2xl bg-tile text-laranja-texto"
                >
                  {r.icone}
                </span>
                <Numeral n={i + 1} className="text-3xl" />
              </div>
              <div>
                <h2 className="font-display text-lg leading-tight font-bold text-titulo">
                  {r.titulo}
                </h2>
                <p className="mt-1 inline-flex rounded-full bg-chip px-2 py-0.5 text-[11px] font-semibold text-chip-tinta">
                  {r.produz}
                </p>
              </div>
              <p className="text-sm leading-relaxed text-cinza">{r.descricao}</p>
              <span className="mt-auto inline-flex items-center gap-1.5 pt-2 text-xs font-extrabold tracking-widest text-laranja-texto uppercase">
                {r.cta}
                <span aria-hidden="true">→</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {/* O aviso é da FERRAMENTA, e por isso fica na entrada e em cada tela: o
          aluno pode chegar direto numa rotina por link, sem passar por aqui. */}
      <section aria-labelledby="o-que-este-simulador-nao-e" className="mt-8">
        <h2
          id="o-que-este-simulador-nao-e"
          className="font-display text-base font-bold text-titulo"
        >
          O que este simulador não é
        </h2>
        <ul className="mt-3 grid gap-3 sm:grid-cols-2">
          <Aviso titulo="Nada é criado de verdade">
            Nenhum CNPJ é criado, nenhum DAS é registrado e nenhuma declaração chega à Receita
            Federal. Os documentos saem com marca d'água e sem validade jurídica.
          </Aviso>
          <Aviso titulo="Nunca peça senha real">
            As telas de acesso imitam o gov.br e o PGMEI para você reconhecê-las depois — mas não
            pedem, não validam e não guardam senha de nenhum serviço.{" "}
            <strong className="text-tinta">Não digite senhas reais aqui.</strong>
          </Aviso>
          <Aviso titulo="Nada fica salvo">
            O que você preenche vive só nesta aba: recarregar a página começa de novo.
          </Aviso>
          <Aviso titulo="Onde é o processo real">
            O MEI de verdade se abre em{" "}
            <a
              href="https://www.gov.br/empresas-e-negocios/pt-br/empreendedor"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-link underline"
            >
              gov.br/empreendedor
              <span className="sr-only"> (abre em outra aba)</span>
            </a>
            .
          </Aviso>
        </ul>
      </section>
    </main>
  );
}

function Aviso({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <li className="surface border-l-4 border-l-laranja p-4">
      <p className="text-xs font-bold tracking-wide text-titulo uppercase">{titulo}</p>
      <p className="mt-1 text-sm leading-relaxed text-cinza">{children}</p>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Os ícones — SVG, não emoji
// ---------------------------------------------------------------------------
// O app de origem (e a primeira versão desta tela) usava 🪪 🧾 📄. Emoji é
// tipografia do SISTEMA: muda de forma e de cor em cada plataforma, não herda a
// cor do texto, e no Chrome headless sai apagado — foi assim que apareceu na
// captura. Os ícones do hub são SVG com `currentColor`, como os de `icones.tsx`.

function IconeCarteira() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="size-6" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <circle cx="8" cy="11" r="2" />
      <path d="M5 16c.6-1.4 1.8-2 3-2s2.4.6 3 2M14 10h5M14 14h3" />
    </svg>
  );
}

function IconeBoleto() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="size-6" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 8v8M10 8v8M13 8v8M17 8v8" />
    </svg>
  );
}

function IconeDeclaracao() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="size-6" aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h4" />
    </svg>
  );
}
